// rules.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { initQuestions } from '../../functions/setUserQuestions';
import { selectAdminToken } from '../../admin/sessionSlice';
import { processQuestions } from '../../admin/questionSlice';
import { setBackButtonUrl } from '../uiSlice';

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const step = Number(params.step || '1');
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user, error } = useSelector((state) => state.auth);
  const [redirect, setRedirect] = useState('/welcome');
  const adminToken = useSelector(selectAdminToken);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!themeData || !user || !adminToken) return;

    // Welcome / full-house themes have no quiz config (no noOfQuestion) — skip setUserQuestions.
    const baseQuestions = parseInt(
      themeData.noOfQuestion ?? themeData.no_of_questions,
      10
    );
    const needsQuizInit = Number.isFinite(baseQuestions) && baseQuestions >= 1;

    setIsLoading(true);

    if (!needsQuizInit) {
      return;
    }

    dispatch(processQuestions())
      .unwrap()
      .then(() => {
        return initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        });
      })
      .then((data) => {
        console.log('Init response:', data);
      })
      .catch((err) => {
        console.error('Error in process→init chain:', err);
      });
  }, [themeData, user, adminToken, dispatch]);

  useEffect(() => {
    if (!themeData) return;
    setRedirect('/welcome');
  }, [themeData]);

  useEffect(() => {
    dispatch(setBackButtonUrl('/login?&save=true'));
  }, [status, user, dispatch]);

  const rulesTextColor = useMemo(() => {
    const raw =
      themeData?.landing_page_title_color ??
      themeData?.landing_page_title_colour;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const nextButtonTextColor = useMemo(() => {
    const raw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const ruleTexts = useMemo(() => {
    const rulesFromTheme = Array.isArray(themeData?.rules)
      ? themeData.rules
      : typeof themeData?.rules === 'string'
      ? themeData.rules.split(/\r?\n/).filter(Boolean)
      : [];

    if (rulesFromTheme.length) return rulesFromTheme;

    return [
      'You are trapped in a room which is a mind palace.',
      'Clues will appear as you progress. Navigate through the items in the room.',
      'Think smart. Think fast. Think together.',
      'Score maximum in minimum amount of time to be on the leaderboard.',
    ];
  }, [themeData]);

  const stepHeading = step === 1 ? 'Rules' : 'More Rules';
  const stepContent = step === 1
    ? ruleTexts.slice(0, Math.ceil(ruleTexts.length / 2))
    : ruleTexts.slice(Math.ceil(ruleTexts.length / 2));

  const nextStep = step === 1 ? 2 : null;
  const buttonLabel = nextStep ? 'Next' : 'Start Game';

  const handleNextClick = (e) => {
    e.preventDefault();
    if (nextStep) {
      navigate(`/rules/${nextStep}`);
    } else {
      navigate(redirect);
    }
  };

  if (!isLoading) {
    return (
      <div className='quiz-loader-container'>
        <div className="quiz-loader"></div>
      </div>
    );
  }

  return (
    <>
      <div className="user-rules-page">
        <div className="user-rules-stack">
          <fieldset
            className="user-rules-fieldset"
            style={rulesTextColor ? { color: rulesTextColor } : undefined}
          >
            <legend className="user-rules-heading">{stepHeading}</legend>
            <ul className="user-rules-list">
              {stepContent.map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
            </ul>
          </fieldset>
          <button
            type="button"
            className="user-next-button"
            onClick={handleNextClick}
            style={nextButtonTextColor ? { color: nextButtonTextColor } : undefined}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export default UserRules;